import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Pencil, Sparkles, Palette, Download, Printer } from 'lucide-react';
import StyleTransferModal from './StyleTransferModal';

export default function ImageLightbox({
  images,
  initialIndex,
  onClose,
  onDrawOnThis,
  onEditWithRoss,
  onStyleTransfer,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [showStyleTransfer, setShowStyleTransfer] = useState(false);
  const current = images[index];

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIndex((i) => Math.min(images.length - 1, i + 1)), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  if (!current) return null;

  const ts = current.timestamp
    ? new Date(typeof current.timestamp === 'number' ? current.timestamp : current.timestamp.seconds * 1000)
    : null;

  const handleDraw = () => {
    onClose();
    onDrawOnThis?.(current.fullResImageUrl || current.imageUrl);
  };

  const handleEdit = () => {
    onClose();
    onEditWithRoss?.(current.fullResImageUrl || current.imageUrl);
  };

  const handleStyleSelect = (style) => {
    setShowStyleTransfer(false);
    onClose();
    onStyleTransfer?.(current.fullResImageUrl || current.imageUrl, style);
  };

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <TopBar>
        <MetaInfo>
          <AuthorName>{current.displayName || 'Unknown'}</AuthorName>
          {ts && <Timestamp>{ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Timestamp>}
        </MetaInfo>
        <CloseBtn onClick={onClose}><X size={22} /></CloseBtn>
      </TopBar>

      <ImageArea onClick={onClose}>
        {index > 0 && (
          <NavBtn $side="left" onClick={(e) => { e.stopPropagation(); prev(); }}>
            <ChevronLeft size={28} />
          </NavBtn>
        )}
        <MainImage
          src={current.fullResImageUrl || current.imageUrl}
          alt=""
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
        {index < images.length - 1 && (
          <NavBtn $side="right" onClick={(e) => { e.stopPropagation(); next(); }}>
            <ChevronRight size={28} />
          </NavBtn>
        )}
      </ImageArea>

      <Counter>{index + 1} / {images.length}</Counter>

      <ActionBar>
        <ActionBtn onClick={handleDraw}>
          <Pencil size={18} />
          <span>Draw</span>
        </ActionBtn>
        <ActionBtn onClick={handleEdit}>
          <Sparkles size={18} />
          <span>Edit</span>
        </ActionBtn>
        <ActionBtn onClick={() => setShowStyleTransfer(true)}>
          <Palette size={18} />
          <span>Style</span>
        </ActionBtn>
        <ActionBtn onClick={() => window.open(`https://app.domoreworld.com/customize-and-order?imageUrl=${encodeURIComponent(current.fullResImageUrl || current.imageUrl)}`, '_blank')}>
          <Download size={18} />
          <span>Save</span>
        </ActionBtn>
        <ActionBtn onClick={() => window.open(`https://app.domoreworld.com/customize-and-order?imageUrl=${encodeURIComponent(current.fullResImageUrl || current.imageUrl)}`, '_blank')}>
          <Printer size={18} />
          <span>Print</span>
        </ActionBtn>
      </ActionBar>

      <AnimatePresence>
        {showStyleTransfer && (
          <StyleTransferModal
            imageUrl={current.fullResImageUrl || current.imageUrl}
            onSelect={handleStyleSelect}
            onClose={() => setShowStyleTransfer(false)}
          />
        )}
      </AnimatePresence>
    </Overlay>
  );
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 400;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top));
  flex-shrink: 0;
`;

const MetaInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #fff;
`;

const Timestamp = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const CloseBtn = styled.button`
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.8);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const ImageArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 16px;
  cursor: pointer;
`;

const MainImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  cursor: default;
  user-select: none;
`;

const NavBtn = styled.button`
  position: absolute;
  ${({ $side }) => ($side === 'left' ? 'left: 8px;' : 'right: 8px;')}
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.15s;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const Counter = styled.div`
  text-align: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  padding: 8px 0 4px;
  flex-shrink: 0;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  flex-shrink: 0;
`;

const ActionBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  min-height: 44px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 600;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;
